import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, RefreshCw, Plus, Edit2, Trash2, Download, FileText,
  Calendar, DollarSign, Clock, CheckCircle, AlertCircle, XCircle,
  Sparkles, Send, Copy, ExternalLink, Filter, ChevronDown, ChevronUp,
  Building2, Target, Loader2, Save, X, Eye, FileCheck, Wand2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_KEY = 'lfm_grants';
const GRANTS_DB_KEY = 'lfm_grants_database';
const LAST_REFRESH_KEY = 'lfm_grants_last_refresh';

const colors = {
  forestGreen: '#1a4a2e',
  gold: '#c9a84c',
  lightGreen: '#2d6a4f',
  cream: '#faf8f5'
};

const GRANT_CATEGORIES = [
  'All Categories',
  'Food Security',
  'Community Development',
  'Faith-Based',
  'Youth Programs',
  'Emergency Assistance',
  'Health & Wellness',
  'Education',
  'Housing',
  'General Operating'
];

const GRANT_STATUSES = [
  { value: 'discovered', label: 'Discovered', color: 'bg-blue-100 text-blue-700' },
  { value: 'researching', label: 'Researching', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'writing', label: 'Writing', color: 'bg-purple-100 text-purple-700' },
  { value: 'ready', label: 'Ready to Submit', color: 'bg-orange-100 text-orange-700' },
  { value: 'submitted', label: 'Submitted', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'awarded', label: 'Awarded', color: 'bg-green-100 text-green-700' },
  { value: 'denied', label: 'Denied', color: 'bg-red-100 text-red-700' },
  { value: 'expired', label: 'Expired', color: 'bg-gray-100 text-gray-500' }
];

// Ministry profile for pre-filling applications
const MINISTRY_PROFILE = {
  name: 'Living Fruit Ministry',
  ein: '[EIN NUMBER]',
  address: '[MINISTRY ADDRESS]',
  city: 'Kissimmee',
  state: 'Florida',
  zip: '[ZIP CODE]',
  phone: '[PHONE NUMBER]',
  email: 'livingfruitministry@gmail.com',
  website: 'livingfruitministry.org',
  mission: 'Living Fruit Ministry is a faith-based organization dedicated to serving the Central Florida community through food distribution, community outreach, spiritual support, and educational programs. We believe in nurturing both the physical and spiritual needs of our neighbors.',
  founded: '[YEAR FOUNDED]',
  taxExempt: '501(c)(3)',
  serviceArea: 'Osceola, Orange, Polk, and Brevard Counties, Florida',
  programs: [
    'Weekly Food Pantry Distribution',
    'Community Outreach Events',
    'Prayer Services',
    'Business Education Classes',
    'Youth Programs'
  ]
};

export default function AdminGrantWriter() {
  // Grants database (discovered grants)
  const [grantsDatabase, setGrantsDatabase] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Application tracking
  const [applications, setApplications] = useState([]);
  
  // UI state
  const [activeTab, setActiveTab] = useState('discover'); // discover, applications, writer
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All Categories');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // view, edit, write
  const [selectedGrant, setSelectedGrant] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  
  // AI Writer state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [writerPrompt, setWriterPrompt] = useState('');

  // Load from localStorage
  useEffect(() => {
    const storedDb = localStorage.getItem(GRANTS_DB_KEY);
    const storedApps = localStorage.getItem(STORAGE_KEY);
    const storedRefresh = localStorage.getItem(LAST_REFRESH_KEY);
    
    if (storedDb) setGrantsDatabase(JSON.parse(storedDb));
    if (storedApps) setApplications(JSON.parse(storedApps));
    if (storedRefresh) setLastRefresh(new Date(storedRefresh));
  }, []);

  // Save functions
  const saveDatabase = (data) => {
    setGrantsDatabase(data);
    localStorage.setItem(GRANTS_DB_KEY, JSON.stringify(data));
  };

  const saveApplications = (data) => {
    setApplications(data);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  };

  // Check if refresh needed (monthly)
  const needsRefresh = useMemo(() => {
    if (!lastRefresh) return true;
    const daysSinceRefresh = (Date.now() - new Date(lastRefresh).getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceRefresh >= 30;
  }, [lastRefresh]);

  // Refresh grants database with AI
  const refreshGrantsDatabase = async () => {
    setIsRefreshing(true);
    try {
      toast.info('AI is searching for Florida grants... This may take a moment.');
      
      const response = await axios.post(`${API}/grants/search-grants`);
      
      if (response.data.success && response.data.grants) {
        // Merge with existing, avoiding duplicates
        const existingIds = grantsDatabase.map(g => g.id);
        const newGrants = response.data.grants.filter(g => !existingIds.includes(g.id));
        const merged = [...grantsDatabase, ...newGrants];
        
        saveDatabase(merged);
        setLastRefresh(new Date());
        localStorage.setItem(LAST_REFRESH_KEY, new Date().toISOString());
        
        toast.success(`Found ${response.data.grants.length} grants! ${newGrants.length} new.`);
      } else {
        toast.error(response.data.error || 'No grants found');
      }
    } catch (error) {
      console.error('Grant search failed:', error);
      toast.error('Failed to search for grants');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Filter grants database
  const filteredGrants = useMemo(() => {
    let filtered = [...grantsDatabase];
    
    if (categoryFilter !== 'All Categories') {
      filtered = filtered.filter(g => g.category === categoryFilter);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(g => 
        g.name?.toLowerCase().includes(query) ||
        g.organization?.toLowerCase().includes(query) ||
        g.description?.toLowerCase().includes(query)
      );
    }
    
    return filtered.sort((a, b) => {
      // Sort by deadline (soonest first), then by amount (highest first)
      if (a.deadline && b.deadline) {
        return new Date(a.deadline) - new Date(b.deadline);
      }
      return (b.maxAmount || 0) - (a.maxAmount || 0);
    });
  }, [grantsDatabase, categoryFilter, searchQuery]);

  // Filter applications
  const filteredApplications = useMemo(() => {
    let filtered = [...applications];
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => a.status === statusFilter);
    }
    
    if (searchQuery && activeTab === 'applications') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        a.grantName?.toLowerCase().includes(query) ||
        a.organization?.toLowerCase().includes(query)
      );
    }
    
    return filtered.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [applications, statusFilter, searchQuery, activeTab]);

  // Start application from grant
  const startApplication = (grant) => {
    const newApp = {
      id: `app-${Date.now()}`,
      grantId: grant.id,
      grantName: grant.name,
      organization: grant.organization,
      deadline: grant.deadline,
      maxAmount: grant.maxAmount,
      requestedAmount: '',
      status: 'researching',
      content: '',
      notes: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Pre-filled ministry info
      ministryInfo: { ...MINISTRY_PROFILE }
    };
    
    saveApplications([...applications, newApp]);
    setSelectedApplication(newApp);
    setActiveTab('applications');
    toast.success('Application started!');
  };

  // Update application status
  const updateApplicationStatus = (appId, newStatus) => {
    const updated = applications.map(a => 
      a.id === appId ? { ...a, status: newStatus, updatedAt: new Date().toISOString() } : a
    );
    saveApplications(updated);
    toast.success('Status updated!');
  };

  // AI Grant Writer
  const generateGrantContent = async (prompt, application) => {
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/grants/write-grant`, {
        prompt,
        grantName: application?.grantName || 'General Grant',
        ministryProfile: MINISTRY_PROFILE,
        requestedAmount: application?.requestedAmount || ''
      });
      
      if (response.data.success) {
        setGeneratedContent(response.data.content);
        toast.success('Content generated!');
      } else {
        toast.error(response.data.error || 'Generation failed');
      }
    } catch (error) {
      console.error('Grant writing failed:', error);
      toast.error('Failed to generate content');
    } finally {
      setIsGenerating(false);
    }
  };

  // Save generated content to application
  const saveContentToApplication = (appId, content) => {
    const updated = applications.map(a => 
      a.id === appId ? { ...a, content: a.content + '\n\n' + content, updatedAt: new Date().toISOString() } : a
    );
    saveApplications(updated);
    toast.success('Content saved to application!');
  };

  // Delete application
  const deleteApplication = (appId) => {
    if (window.confirm('Delete this application?')) {
      saveApplications(applications.filter(a => a.id !== appId));
      toast.success('Application deleted');
    }
  };

  // Export applications
  const exportApplications = () => {
    const data = applications.map(a => ({
      'Grant Name': a.grantName,
      'Organization': a.organization,
      'Status': a.status,
      'Requested Amount': a.requestedAmount,
      'Deadline': a.deadline,
      'Created': a.createdAt,
      'Updated': a.updatedAt
    }));
    
    const headers = Object.keys(data[0] || {});
    const csv = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `grant-applications-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Applications exported!');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const s = GRANT_STATUSES.find(st => st.value === status);
    return s ? s : { label: status, color: 'bg-gray-100 text-gray-600' };
  };

  // Stats
  const stats = useMemo(() => ({
    totalGrants: grantsDatabase.length,
    totalApplications: applications.length,
    submitted: applications.filter(a => a.status === 'submitted').length,
    awarded: applications.filter(a => a.status === 'awarded').length,
    awardedAmount: applications.filter(a => a.status === 'awarded').reduce((sum, a) => sum + (parseFloat(a.requestedAmount) || 0), 0),
    pending: applications.filter(a => ['researching', 'writing', 'ready'].includes(a.status)).length
  }), [grantsDatabase, applications]);

  return (
    <div className="pb-8" data-testid="admin-grant-writer">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl flex items-center gap-3" style={{ color: colors.forestGreen }}>
            <Wand2 size={32} />
            Grant Writer & Discovery
          </h1>
          <p className="text-gray-500 mt-1">Find grants, write applications, and track submissions</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Calendar size={16} />
          Last refreshed: {lastRefresh ? lastRefresh.toLocaleDateString() : 'Never'}
          {needsRefresh && <span className="text-orange-500 font-medium">(Refresh recommended)</span>}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4" style={{ borderColor: colors.forestGreen }}>
          <p className="text-sm text-gray-500">Grants Found</p>
          <p className="text-2xl font-bold" style={{ color: colors.forestGreen }}>{stats.totalGrants}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4" style={{ borderColor: colors.gold }}>
          <p className="text-sm text-gray-500">Applications</p>
          <p className="text-2xl font-bold" style={{ color: colors.gold }}>{stats.totalApplications}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-cyan-500">
          <p className="text-sm text-gray-500">Submitted</p>
          <p className="text-2xl font-bold text-cyan-600">{stats.submitted}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-green-500">
          <p className="text-sm text-gray-500">Awarded</p>
          <p className="text-2xl font-bold text-green-600">{stats.awarded}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-purple-500">
          <p className="text-sm text-gray-500">Total Awarded</p>
          <p className="text-2xl font-bold text-purple-600">{formatCurrency(stats.awardedAmount)}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6 w-fit">
        {[
          { id: 'discover', label: 'Discover Grants', icon: Search },
          { id: 'applications', label: 'My Applications', icon: FileText },
          { id: 'writer', label: 'AI Writer', icon: Sparkles }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === tab.id 
                ? 'text-white shadow-md' 
                : 'text-gray-600 hover:bg-gray-200'
            }`}
            style={activeTab === tab.id ? { backgroundColor: colors.forestGreen } : {}}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Discover Tab */}
      {activeTab === 'discover' && (
        <div>
          {/* Actions */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <Button
              onClick={refreshGrantsDatabase}
              disabled={isRefreshing}
              className="text-white"
              style={{ backgroundColor: colors.gold }}
              data-testid="refresh-grants-btn"
            >
              {isRefreshing ? (
                <Loader2 size={18} className="mr-2 animate-spin" />
              ) : (
                <RefreshCw size={18} className="mr-2" />
              )}
              {isRefreshing ? 'Searching...' : 'AI Search for Grants'}
            </Button>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg"
              data-testid="category-filter"
            >
              {GRANT_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search grants..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-grants"
                />
              </div>
            </div>
          </div>

          {/* Refreshing Overlay */}
          {isRefreshing && (
            <div className="bg-white rounded-xl shadow-md p-8 mb-6 text-center">
              <Loader2 size={48} className="animate-spin mx-auto mb-4" style={{ color: colors.gold }} />
              <p className="text-lg font-medium" style={{ color: colors.forestGreen }}>
                AI is searching for Florida non-profit grants...
              </p>
              <p className="text-gray-500 text-sm mt-2">
                Looking for food security, community development, faith-based, and general operating grants
              </p>
            </div>
          )}

          {/* Grants Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredGrants.map((grant) => (
              <motion.div
                key={grant.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div 
                  className="px-4 py-3"
                  style={{ backgroundColor: colors.forestGreen }}
                >
                  <h3 className="text-white font-semibold truncate">{grant.name}</h3>
                  <p className="text-white/70 text-sm truncate">{grant.organization}</p>
                </div>
                
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                      {grant.category}
                    </span>
                    {grant.maxAmount && (
                      <span className="font-bold" style={{ color: colors.gold }}>
                        Up to {formatCurrency(grant.maxAmount)}
                      </span>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-600 line-clamp-2">{grant.description}</p>
                  
                  {grant.deadline && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar size={14} className="text-gray-400" />
                      <span className={`${new Date(grant.deadline) < new Date() ? 'text-red-500' : 'text-gray-600'}`}>
                        Deadline: {new Date(grant.deadline).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  
                  {grant.eligibility && (
                    <p className="text-xs text-gray-500 italic">{grant.eligibility}</p>
                  )}
                </div>
                
                <div className="px-4 py-3 bg-gray-50 flex items-center justify-between border-t">
                  {grant.url && (
                    <a
                      href={grant.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink size={14} />
                      View Details
                    </a>
                  )}
                  <Button
                    size="sm"
                    onClick={() => startApplication(grant)}
                    className="text-white"
                    style={{ backgroundColor: colors.forestGreen }}
                  >
                    <Plus size={14} className="mr-1" />
                    Start Application
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>

          {filteredGrants.length === 0 && !isRefreshing && (
            <div className="bg-white rounded-xl shadow-md p-12 text-center">
              <Search size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-700">No grants found</h3>
              <p className="text-gray-500 mt-2">
                Click "AI Search for Grants" to discover available funding opportunities
              </p>
            </div>
          )}
        </div>
      )}

      {/* Applications Tab */}
      {activeTab === 'applications' && (
        <div>
          {/* Actions */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg"
              data-testid="status-filter"
            >
              <option value="all">All Statuses</option>
              {GRANT_STATUSES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>

            <Button
              onClick={exportApplications}
              variant="outline"
              style={{ borderColor: colors.forestGreen, color: colors.forestGreen }}
              data-testid="export-apps-btn"
            >
              <Download size={18} className="mr-2" />
              Export
            </Button>
          </div>

          {/* Applications Table */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: colors.forestGreen }} className="text-white">
                  <th className="px-4 py-3 text-left text-sm font-medium">Grant</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Organization</th>
                  <th className="px-4 py-3 text-center text-sm font-medium">Status</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Deadline</th>
                  <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                      <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-lg">No applications yet</p>
                      <p className="text-sm">Find grants and start an application</p>
                    </td>
                  </tr>
                ) : (
                  filteredApplications.map((app, i) => {
                    const status = getStatusBadge(app.status);
                    return (
                      <tr key={app.id} className={`border-b ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{app.grantName}</p>
                          <p className="text-xs text-gray-500">Created: {new Date(app.createdAt).toLocaleDateString()}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{app.organization}</td>
                        <td className="px-4 py-3 text-center">
                          <select
                            value={app.status}
                            onChange={(e) => updateApplicationStatus(app.id, e.target.value)}
                            className={`text-xs font-medium px-2 py-1 rounded-full ${status.color}`}
                          >
                            {GRANT_STATUSES.map(s => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Input
                            type="number"
                            value={app.requestedAmount}
                            onChange={(e) => {
                              const updated = applications.map(a => 
                                a.id === app.id ? { ...a, requestedAmount: e.target.value } : a
                              );
                              saveApplications(updated);
                            }}
                            placeholder="$0"
                            className="w-28 text-right"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {app.deadline && (
                            <span className={new Date(app.deadline) < new Date() ? 'text-red-500' : 'text-gray-600'}>
                              {new Date(app.deadline).toLocaleDateString()}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => {
                                setSelectedApplication(app);
                                setActiveTab('writer');
                              }}
                              className="p-2 rounded-lg hover:bg-purple-100 text-purple-600"
                              title="Write with AI"
                            >
                              <Sparkles size={16} />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedApplication(app);
                                setShowModal(true);
                                setModalMode('edit');
                              }}
                              className="p-2 rounded-lg hover:bg-gray-200 text-gray-600"
                              title="Edit"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => deleteApplication(app.id)}
                              className="p-2 rounded-lg hover:bg-red-100 text-red-500"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AI Writer Tab */}
      {activeTab === 'writer' && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Writer Input */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: colors.forestGreen }}>
              <Sparkles size={20} />
              AI Grant Writer
            </h2>

            {/* Select Application */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Application (optional)
              </label>
              <select
                value={selectedApplication?.id || ''}
                onChange={(e) => {
                  const app = applications.find(a => a.id === e.target.value);
                  setSelectedApplication(app || null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="select-application"
              >
                <option value="">General writing (no specific grant)</option>
                {applications.map(app => (
                  <option key={app.id} value={app.id}>{app.grantName}</option>
                ))}
              </select>
            </div>

            {/* Quick Prompts */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quick Prompts
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  'Organization Summary',
                  'Need Statement',
                  'Program Description',
                  'Goals & Objectives',
                  'Budget Justification',
                  'Sustainability Plan',
                  'Community Impact'
                ].map(prompt => (
                  <button
                    key={prompt}
                    onClick={() => setWriterPrompt(`Write a ${prompt.toLowerCase()} section for a grant application.`)}
                    className="text-xs px-3 py-1 rounded-full border hover:bg-gray-100"
                    style={{ borderColor: colors.gold, color: colors.forestGreen }}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Prompt */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                What would you like to write?
              </label>
              <textarea
                value={writerPrompt}
                onChange={(e) => setWriterPrompt(e.target.value)}
                placeholder="E.g., Write a compelling need statement explaining why our food pantry program is essential for the community..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg min-h-[120px]"
                data-testid="writer-prompt"
              />
            </div>

            <Button
              onClick={() => generateGrantContent(writerPrompt, selectedApplication)}
              disabled={isGenerating || !writerPrompt}
              className="w-full text-white"
              style={{ backgroundColor: colors.forestGreen }}
              data-testid="generate-btn"
            >
              {isGenerating ? (
                <Loader2 size={18} className="mr-2 animate-spin" />
              ) : (
                <Sparkles size={18} className="mr-2" />
              )}
              {isGenerating ? 'Generating...' : 'Generate Content'}
            </Button>
          </div>

          {/* Generated Output */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2" style={{ color: colors.forestGreen }}>
                <FileText size={20} />
                Generated Content
              </h2>
              {generatedContent && (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(generatedContent);
                      toast.success('Copied to clipboard!');
                    }}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                    title="Copy"
                  >
                    <Copy size={18} />
                  </button>
                  {selectedApplication && (
                    <button
                      onClick={() => saveContentToApplication(selectedApplication.id, generatedContent)}
                      className="p-2 rounded-lg hover:bg-green-100 text-green-600"
                      title="Save to application"
                    >
                      <Save size={18} />
                    </button>
                  )}
                </div>
              )}
            </div>

            {isGenerating ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 size={40} className="animate-spin" style={{ color: colors.gold }} />
              </div>
            ) : generatedContent ? (
              <div className="prose prose-sm max-w-none">
                <div className="bg-gray-50 rounded-lg p-4 max-h-[500px] overflow-y-auto whitespace-pre-wrap text-sm">
                  {generatedContent}
                </div>
              </div>
            ) : (
              <div className="text-center py-20 text-gray-400">
                <Sparkles size={48} className="mx-auto mb-4" />
                <p>Generated content will appear here</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ministry Profile Panel (collapsible) */}
      <div className="mt-6 bg-white rounded-xl shadow-md overflow-hidden">
        <button
          onClick={() => setShowModal(true)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
        >
          <div className="flex items-center gap-3">
            <Building2 size={20} style={{ color: colors.forestGreen }} />
            <span className="font-medium">Ministry Profile (Pre-fill Data)</span>
          </div>
          <span className="text-sm text-gray-500">Click to view/edit</span>
        </button>
      </div>

      {/* Application Edit Modal */}
      <AnimatePresence>
        {showModal && selectedApplication && modalMode === 'edit' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div 
                className="px-6 py-4 flex items-center justify-between sticky top-0"
                style={{ backgroundColor: colors.forestGreen }}
              >
                <h2 className="text-xl font-bold text-white">{selectedApplication.grantName}</h2>
                <button onClick={() => setShowModal(false)} className="text-white/80 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={selectedApplication.status}
                      onChange={(e) => {
                        const updated = { ...selectedApplication, status: e.target.value };
                        setSelectedApplication(updated);
                        const apps = applications.map(a => a.id === updated.id ? updated : a);
                        saveApplications(apps);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      {GRANT_STATUSES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Requested Amount</label>
                    <Input
                      type="number"
                      value={selectedApplication.requestedAmount}
                      onChange={(e) => {
                        const updated = { ...selectedApplication, requestedAmount: e.target.value };
                        setSelectedApplication(updated);
                        const apps = applications.map(a => a.id === updated.id ? updated : a);
                        saveApplications(apps);
                      }}
                      placeholder="$0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Application Content</label>
                  <textarea
                    value={selectedApplication.content}
                    onChange={(e) => {
                      const updated = { ...selectedApplication, content: e.target.value };
                      setSelectedApplication(updated);
                      const apps = applications.map(a => a.id === updated.id ? updated : a);
                      saveApplications(apps);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg min-h-[300px] font-mono text-sm"
                    placeholder="Paste or write your application content here..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={selectedApplication.notes}
                    onChange={(e) => {
                      const updated = { ...selectedApplication, notes: e.target.value };
                      setSelectedApplication(updated);
                      const apps = applications.map(a => a.id === updated.id ? updated : a);
                      saveApplications(apps);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg min-h-[100px]"
                    placeholder="Internal notes..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button onClick={() => setShowModal(false)} variant="outline" className="flex-1">
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      setActiveTab('writer');
                      setShowModal(false);
                    }}
                    className="flex-1 text-white"
                    style={{ backgroundColor: colors.forestGreen }}
                  >
                    <Sparkles size={18} className="mr-2" />
                    Write with AI
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
